import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";
import {
  CheckCircle, Package, Truck, MapPin, CreditCard, X, ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STEPS = [
  { key: "placed",           label: "Order Placed",      icon: Package },
  { key: "accepted",         label: "Confirmed",         icon: CheckCircle },
  { key: "packed",           label: "Packed",            icon: Package },
  { key: "partially_shipped",label: "Partially Shipped", icon: Truck },
  { key: "shipped",          label: "Shipped",           icon: Truck },
  { key: "out_for_delivery", label: "Out for Delivery",  icon: Truck },
  { key: "delivered",        label: "Delivered",         icon: CheckCircle },
];

const STATUS_ORDER = ["placed", "accepted", "packed", "partially_shipped", "shipped", "out_for_delivery", "delivered"];

function fmt(n) {
  if (!n && n !== 0) return "0";
  return Number(n).toLocaleString("en-IN");
}

function StatusBadge({ status }) {
  const colors = {
    placed:            "bg-blue-100 text-blue-700",
    accepted:          "bg-indigo-100 text-indigo-700",
    packed:            "bg-purple-100 text-purple-700",
    partially_shipped: "bg-amber-100 text-amber-700",
    shipped:           "bg-amber-100 text-amber-700",
    out_for_delivery:  "bg-orange-100 text-orange-700",
    delivered:         "bg-green-100 text-green-700",
    cancelled:         "bg-red-100 text-red-700",
  };
  const labels = {
    placed: "Order Placed", accepted: "Confirmed", packed: "Packed",
    partially_shipped: "Partially Shipped",
    shipped: "Shipped", out_for_delivery: "Out for Delivery",
    delivered: "Delivered", cancelled: "Cancelled",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-bold ${colors[status] || "bg-slate-100 text-slate-700"}`}>
      {labels[status] || status}
    </span>
  );
}

function ItemStatusTag({ status }) {
  if (!status) return null;
  const colors = {
    placed:   "bg-blue-50 text-blue-600 border-blue-200",
    accepted: "bg-indigo-50 text-indigo-600 border-indigo-200",
    packed:   "bg-cyan-50 text-cyan-700 border-cyan-200",
    shipped:  "bg-amber-50 text-amber-700 border-amber-200",
    cancelled:"bg-slate-100 text-slate-500 border-slate-200",
  };
  const labels = { placed:"Placed", accepted:"Accepted", packed:"Packed", shipped:"Shipped", cancelled:"Cancelled" };
  return (
    <span className={`inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border ${colors[status] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
      {labels[status] || status}
    </span>
  );
}

export default function OrderTrackingPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/orders/${id}`)
      .then(res => setOrder(res.data))
      .catch(err => setError(err.response?.data?.detail || "Order not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 mb-2">Order Not Found</h2>
          <p className="text-sm text-slate-500 mb-6">{error || "We couldn't find this order. Please check the order ID."}</p>
          <Link to="/" className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:underline text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const isCancelled = order.order_status === "cancelled";
  const currentStep = STATUS_ORDER.indexOf(order.order_status);

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex-shrink-0">
            <BrandLogo size="md" />
          </Link>
          <span className="text-slate-300 text-lg">|</span>
          <span className="text-sm font-semibold text-slate-600">Order Tracking</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Order Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5" data-testid="order-header">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-lg font-black text-slate-900">Order #{order.order_number}</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Placed on {new Date(order.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={order.order_status} />
              {order.payment_status === "paid" && (
                <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full border border-green-200">
                  Paid
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Cancelled banner */}
        {isCancelled && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-center gap-3" data-testid="order-cancelled-banner">
            <X className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-700">Order Cancelled</p>
              <p className="text-xs text-red-500 mt-0.5">This order has been cancelled.</p>
            </div>
          </div>
        )}

        {/* Status Stepper */}
        {!isCancelled && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5" data-testid="order-status-stepper">
            <h2 className="text-sm font-bold text-slate-700 mb-6">Order Progress</h2>
            <div className="flex items-start justify-between relative">
              {/* Progress bar background */}
              <div className="absolute top-4 left-4 right-4 h-0.5 bg-slate-200" />
              {currentStep >= 0 && (
                <div
                  className="absolute top-4 left-4 h-0.5 bg-blue-500 transition-all duration-700"
                  style={{
                    width: currentStep === 0
                      ? "0%"
                      : `${(currentStep / (ORDER_STEPS.length - 1)) * 100}%`,
                  }}
                />
              )}
              {ORDER_STEPS.map((step, idx) => {
                const done = currentStep >= idx;
                const active = currentStep === idx;
                const StepIcon = step.icon;
                return (
                  <div key={step.key} className="flex flex-col items-center gap-2 z-10 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      done
                        ? "bg-blue-600 border-2 border-blue-600 shadow-md shadow-blue-200"
                        : "bg-white border-2 border-slate-200"
                    }`}>
                      <StepIcon className={`w-3.5 h-3.5 ${done ? "text-white" : "text-slate-300"}`} />
                    </div>
                    <div className="text-center">
                      <p className={`text-[11px] font-semibold leading-tight ${done ? "text-slate-800" : "text-slate-400"}`}>
                        {step.label}
                      </p>
                      {active && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse mx-auto mt-1" />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tracking info (if shipped) */}
        {order.tracking?.awb && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5" data-testid="order-tracking-info">
            <h2 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-500" /> Shipping Details
            </h2>
            <div className="space-y-2 text-sm">
              {order.tracking.courier_name && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Courier</span>
                  <span className="font-semibold text-slate-800">{order.tracking.courier_name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">AWB / Tracking No.</span>
                <span className="font-semibold text-slate-800">{order.tracking.awb}</span>
              </div>
              {order.tracking.estimated_delivery && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Estimated Delivery</span>
                  <span className="font-semibold text-slate-800">{order.tracking.estimated_delivery}</span>
                </div>
              )}
              {order.tracking.tracking_url && (
                <a
                  href={order.tracking.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-blue-600 hover:underline font-semibold mt-1 text-xs"
                >
                  <Truck className="w-3.5 h-3.5" /> Track on courier website
                </a>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Order Items */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5" data-testid="order-items">
            <h2 className="text-sm font-bold text-slate-700 mb-3">Items Ordered ({order.items?.length || 0})</h2>
            <div className="space-y-3">
              {(order.items || []).map((item, i) => (
                <div key={i} className="flex gap-3">
                  {item.image ? (
                    <img src={item.image} alt={item.name} className="w-14 h-14 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-snug">{item.name || item.product_name}</p>
                    {item.variant && <p className="text-[11px] text-slate-400 mt-0.5">{item.variant}</p>}
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-slate-500">Qty: {item.quantity}</span>
                      <span className="text-xs font-bold text-slate-900">
                        ₹{fmt(item.amount || (item.price * item.quantity))}
                      </span>
                    </div>
                    {/* Per-item status + tracking */}
                    {item.item_status && (
                      <div className="mt-1.5 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <ItemStatusTag status={item.item_status} />
                          {item.item_tracking_number && (
                            <span className="text-[10px] text-slate-500 font-mono">AWB: {item.item_tracking_number}</span>
                          )}
                        </div>
                        {item.item_courier && (
                          <p className="text-[10px] text-slate-400">Courier: {item.item_courier}</p>
                        )}
                        {item.item_tracking_url && (
                          <a href={item.item_tracking_url} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] text-blue-600 hover:underline font-semibold inline-flex items-center gap-1">
                            <Truck className="w-2.5 h-2.5" /> Track this item
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 mt-4 pt-3 space-y-1">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Subtotal</span>
                <span>₹{fmt(order.subtotal)}</span>
              </div>
              {order.delivery_charge > 0 && (
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Delivery</span>
                  <span>₹{fmt(order.delivery_charge)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold text-slate-900 pt-1 border-t border-slate-100 mt-1">
                <span>Total</span>
                <span>₹{fmt(order.total)}</span>
              </div>
            </div>
          </div>

          {/* Delivery & Payment */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5" data-testid="order-delivery-address">
              <h2 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" /> Delivery Address
              </h2>
              <p className="text-sm font-semibold text-slate-800">{order.customer_name}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{order.delivery_address}</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5" data-testid="order-payment-info">
              <h2 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-500" /> Payment Info
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Method</span>
                  <span className="font-semibold text-slate-800">{order.payment_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Status</span>
                  <span className={`font-semibold capitalize ${order.payment_status === "paid" ? "text-green-600" : "text-amber-600"}`}>
                    {order.payment_status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors text-sm"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
